import { observer } from 'mobx-react-lite';
import React, { Fragment, useContext, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import { Button, Header, Icon, Label, Modal, Table } from 'semantic-ui-react';

import {
  formatListDisp,
  PROCESS_STATE,
  TICKET_RESERVE_TYPE,
} from '../../../app/common/util/util';
import { IDepositSalesTicket } from '../../../app/models/depositSalesTicket';
import { RootStoreContext } from '../../../app/stores/rootStore';

const DepositSalesTicketList: React.FC = () => {
  const rootStore = useContext(RootStoreContext);
  const {
    submitting,
    depositSalesTickets,
    depositSalesTicketsCount,
    deleteDepositSalesTicket,
    issueDepositSalesTicket,
  } = rootStore.depositSalesTicketStore;
  const { csvData, dlQrTickets, printQrTickets } = rootStore.qrTicketStore;

  const csvInstance = useRef<any | null>(null);
  const [dlModalOpen, setDlModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetRow, setTargetRow] = useState<IDepositSalesTicket | null>(null);

  const handleCsvDownload = (depositSalesTicket: IDepositSalesTicket) => {
    issueDepositSalesTicket(depositSalesTicket).then(() => {
      if (csvInstance && csvInstance.current && csvInstance.current.link) {
        dlQrTickets(depositSalesTicket.salesId).then(() => {
          csvInstance.current.link.click();
          setDlModalOpen(false);
        });
      }
    });
  };

  const handlePrintTicket = (depositSalesTicket: IDepositSalesTicket) => {
    issueDepositSalesTicket(depositSalesTicket).then(() => {
      printQrTickets(depositSalesTicket.salesId).then(() => {
        setPrintModalOpen(false);
      });
    });
  };

  return (
    <Fragment>
      <Label size='large'>
        検索結果 : {depositSalesTicketsCount.toLocaleString()} 件
      </Label>
      <Table celled compact striped selectable size='small'>
        <Table.Header fullWidth>
          <Table.Row>
            <Table.HeaderCell width='1'>ステータス</Table.HeaderCell>
            <Table.HeaderCell>チケットNo</Table.HeaderCell>
            <Table.HeaderCell>委託先</Table.HeaderCell>
            <Table.HeaderCell>
              作成日/
              <br />
              発券日
            </Table.HeaderCell>
            <Table.HeaderCell>入場可能期間</Table.HeaderCell>
            <Table.HeaderCell>テナント</Table.HeaderCell>
            <Table.HeaderCell>券種</Table.HeaderCell>
            <Table.HeaderCell>単価/枚数/金額</Table.HeaderCell>
            <Table.HeaderCell>発券区分</Table.HeaderCell>
            <Table.HeaderCell>QR</Table.HeaderCell>
            <Table.HeaderCell>発券</Table.HeaderCell>
            <Table.HeaderCell>編集</Table.HeaderCell>
            <Table.HeaderCell>明細</Table.HeaderCell>
            <Table.HeaderCell>廃券</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {depositSalesTickets.map(
            (depositSalesTicket: IDepositSalesTicket) => (
              <Table.Row
                key={depositSalesTicket.salesId}
                negative={
                  depositSalesTicket.deleteFlagEnum === PROCESS_STATE.Done
                }
              >
                <Table.Cell>
                  {depositSalesTicket.deleteFlagEnum === PROCESS_STATE.Done ? (
                    <Label color='red' ribbon>
                      廃券
                    </Label>
                  ) : depositSalesTicket.ticketReserveStatusEnum ===
                    PROCESS_STATE.Doing ? (
                    <Label color='yellow' ribbon>
                      一部済
                    </Label>
                  ) : depositSalesTicket.ticketReserveStatusEnum ===
                    PROCESS_STATE.Done ? (
                    <Label color='teal' ribbon>
                      発券済
                    </Label>
                  ) : (
                    ''
                  )}
                </Table.Cell>
                <Table.Cell className='link-button'>
                  <Link
                    to={`/admin/depositsales/detail/${depositSalesTicket.salesId}`}
                  >
                    {depositSalesTicket.ticketNo}
                  </Link>
                </Table.Cell>
                <Table.Cell>{depositSalesTicket.agentName}</Table.Cell>
                <Table.Cell singleLine>
                  {formatListDisp(
                    new Date(depositSalesTicket.registrationDate)
                  )}
                  <br />
                  {depositSalesTicket.ticketReserveTime &&
                    formatListDisp(
                      new Date(depositSalesTicket.ticketReserveTime)
                    )}
                </Table.Cell>
                <Table.Cell singleLine>
                  {depositSalesTicket.startDate &&
                    formatListDisp(new Date(depositSalesTicket.startDate))}
                  ～ <br />
                  {depositSalesTicket.limitMonthDate &&
                    formatListDisp(new Date(depositSalesTicket.limitMonthDate))}
                </Table.Cell>
                <Table.Cell singleLine>
                  {depositSalesTicket.mdTdTicketTypeName}
                </Table.Cell>
                <Table.Cell>{depositSalesTicket.ticketDisplayName}</Table.Cell>
                <Table.Cell singleLine>
                  {depositSalesTicket.unitPrice.toLocaleString()}円<br />
                  {depositSalesTicket.count.toLocaleString()}枚<br />
                  {depositSalesTicket.cashTotal.toLocaleString()}円
                </Table.Cell>
                <Table.Cell>
                  {depositSalesTicket.ticketReserveTypeName}
                </Table.Cell>
                <Table.Cell singleLine>
                  <Modal
                    trigger={
                      <Button
                        content='DL'
                        color='blue'
                        compact
                        disabled={
                          depositSalesTicket.count === 0 ||
                          depositSalesTicket.deleteFlagEnum ===
                            PROCESS_STATE.Done ||
                          depositSalesTicket.ticketReserveTypeEnum ===
                            TICKET_RESERVE_TYPE.Normal
                        }
                        onClick={() => {
                          setTargetRow(depositSalesTicket);
                          setDlModalOpen(true);
                        }}
                      />
                    }
                    open={dlModalOpen}
                    onClose={() => setDlModalOpen(false)}
                    basic
                    size='small'
                  >
                    <Header icon='download' />
                    <Modal.Content>
                      <h3>QR送信して、よろしいでしょうか？</h3>
                    </Modal.Content>
                    <Modal.Actions>
                      <CSVLink
                        data={csvData}
                        filename='data.csv'
                        ref={csvInstance}
                      />
                      <Button
                        basic
                        color='green'
                        inverted
                        floated='right'
                        loading={submitting}
                        onClick={() =>
                          targetRow && handleCsvDownload(targetRow)
                        }
                      >
                        <Icon name='checkmark' />
                        はい
                      </Button>
                      <Button
                        color='red'
                        inverted
                        onClick={() => setDlModalOpen(false)}
                      >
                        <Icon name='remove' />
                        いいえ
                      </Button>
                    </Modal.Actions>
                  </Modal>
                </Table.Cell>
                <Table.Cell singleLine>
                  <Modal
                    trigger={
                      <Button
                        content='発券'
                        color='blue'
                        compact
                        disabled={
                          depositSalesTicket.count === 0 ||
                          depositSalesTicket.deleteFlagEnum ===
                            PROCESS_STATE.Done ||
                          depositSalesTicket.ticketReserveTypeEnum ===
                            TICKET_RESERVE_TYPE.Api
                        }
                        onClick={() => {
                          setTargetRow(depositSalesTicket);
                          setPrintModalOpen(true);
                        }}
                      />
                    }
                    open={printModalOpen}
                    onClose={() => setPrintModalOpen(false)}
                    basic
                    size='small'
                  >
                    <Header icon='print' />
                    <Modal.Content>
                      <h3>発券して、よろしいでしょうか？</h3>
                    </Modal.Content>
                    <Modal.Actions>
                      <Button
                        basic
                        color='green'
                        inverted
                        floated='right'
                        loading={submitting}
                        onClick={() =>
                          targetRow && handlePrintTicket(targetRow)
                        }
                      >
                        <Icon name='checkmark' />
                        はい
                      </Button>
                      <Button
                        color='red'
                        inverted
                        onClick={() => setPrintModalOpen(false)}
                      >
                        <Icon name='remove' />
                        いいえ
                      </Button>
                    </Modal.Actions>
                  </Modal>
                </Table.Cell>
                <Table.Cell singleLine>
                  <Button
                    content='編集'
                    color='blue'
                    compact
                    as={Link}
                    to={
                      depositSalesTicket.ticketReserveStatusEnum ===
                        PROCESS_STATE.Doing ||
                      depositSalesTicket.ticketReserveStatusEnum ===
                        PROCESS_STATE.Done
                        ? `/admin/depositsales/changeLimit/${depositSalesTicket.salesId}`
                        : `/admin/depositsales/edit/${depositSalesTicket.salesId}`
                    }
                    disabled={
                      depositSalesTicket.deleteFlagEnum === PROCESS_STATE.Done
                    }
                  />
                </Table.Cell>
                <Table.Cell singleLine>
                  <Button
                    content='明細'
                    color='green'
                    compact
                    as={Link}
                    to={`/admin/depositsales/qrticket/${depositSalesTicket.salesId}`}
                    disabled={
                      depositSalesTicket.deleteFlagEnum === PROCESS_STATE.Done
                    }
                  />
                </Table.Cell>
                <Table.Cell singleLine>
                  <Modal
                    trigger={
                      <Button
                        content='廃券'
                        color='red'
                        compact
                        disabled={
                          depositSalesTicket.deleteFlagEnum ===
                          PROCESS_STATE.Done
                        }
                        onClick={() => {
                          setTargetRow(depositSalesTicket);
                          setDeleteModalOpen(true);
                        }}
                      />
                    }
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    basic
                    size='small'
                  >
                    <Header icon='trash' />
                    <Modal.Content>
                      <h3>廃券にして、よろしいでしょうか？</h3>
                    </Modal.Content>
                    <Modal.Actions>
                      <Button
                        basic
                        color='green'
                        inverted
                        floated='right'
                        loading={submitting}
                        onClick={() => {
                          targetRow &&
                            deleteDepositSalesTicket(targetRow).then(() =>
                              setDeleteModalOpen(false)
                            );
                        }}
                      >
                        <Icon name='checkmark' />
                        はい
                      </Button>
                      <Button
                        color='red'
                        inverted
                        onClick={() => setDeleteModalOpen(false)}
                      >
                        <Icon name='remove' />
                        いいえ
                      </Button>
                    </Modal.Actions>
                  </Modal>
                </Table.Cell>
              </Table.Row>
            )
          )}
        </Table.Body>
      </Table>
    </Fragment>
  );
};

export default observer(DepositSalesTicketList);
